import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money } from "@/lib/api";
import { splitIncGst, r2 } from "@/lib/priceCalc";

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="tabular font-semibold" data-testid={testid}>
      {value}
    </span>
  </div>
);

/** Work backwards from a GST-inclusive agreed price to the ex-GST figure, then add a
 *  tidy "Negotiated discount" line so the invoice total lands on the agreed price.
 *  No existing calculation is changed — the discount is just another line. */
export default function NegotiatePriceDialog({
  open,
  onOpenChange,
  currentTotal,
  currentSubtotal,
  gstRate = 10,
  onApply,
}) {
  const [agreed, setAgreed] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setAgreed("");
      setReason("");
    }
  }, [open]);

  const agreedNum = Number(agreed) || 0;
  const rate = Number(gstRate) || 0;
  const split = splitIncGst(agreedNum, rate);
  const achievedTotal = split.incGst; // the inc-GST total the invoice will actually show
  const discountExGst = r2(currentSubtotal - split.exGst);
  const discountIncGst = r2(currentTotal - achievedTotal);
  const pennyOff = r2(achievedTotal - agreedNum); // 0, or +/-0.01 on unreachable cents

  const entered = agreed !== "";
  const tooHigh = entered && agreedNum >= currentTotal - 0.005;
  const zeroOrNeg = entered && agreedNum <= 0;
  const valid = entered && agreedNum > 0 && agreedNum < currentTotal - 0.005;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="negotiate-dialog">
        <DialogHeader>
          <DialogTitle>Negotiate price</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between border border-border bg-[#FBF8F2] px-3 py-2">
            <span className="text-muted-foreground">Current total (inc GST)</span>
            <span className="tabular font-semibold" data-testid="negotiate-current-total">
              {money(currentTotal)}
            </span>
          </div>

          <div className="space-y-1.5">
            <Label>Price the customer will pay (inc GST)</Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={agreed}
              onChange={(e) => setAgreed(e.target.value)}
              placeholder="e.g. 600"
              data-testid="negotiate-agreed-input"
              autoFocus
            />
            {tooHigh && (
              <p className="text-xs text-destructive" data-testid="negotiate-warn-high">
                That's the same as or above the current total — enter the lower, agreed price.
              </p>
            )}
            {zeroOrNeg && (
              <p className="text-xs text-destructive">Enter a price above zero.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Reason (optional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. owner approved"
              data-testid="negotiate-reason-input"
            />
          </div>

          {valid && (
            <div
              className="space-y-2 border border-[#E4D9BF] bg-white px-3 py-3"
              data-testid="negotiate-preview"
            >
              <Row label="New ex-GST (set price)" value={money(split.exGst)} testid="negotiate-exgst" />
              <Row label={`GST ${rate}%`} value={money(split.gst)} testid="negotiate-gst" />
              <div className="flex items-center justify-between border-t border-border pt-2 font-semibold">
                <span>New total (inc GST)</span>
                <span className="tabular" data-testid="negotiate-new-total">
                  {money(achievedTotal)}
                </span>
              </div>
              {pennyOff !== 0 && (
                <p className="text-xs text-muted-foreground" data-testid="negotiate-penny-note">
                  Lands on {money(achievedTotal)} — a cent off {money(agreedNum)} so GST stays a
                  true {rate}% of the sale.
                </p>
              )}
              <div
                className="mt-1 bg-[#FDF2EE] px-2 py-1.5 text-xs text-[#9A3412]"
                data-testid="negotiate-discount-line"
              >
                A line <strong>&ldquo;Negotiated discount&rdquo;</strong> of{" "}
                <strong>-{money(discountExGst)}</strong> (ex GST) will be added — that's
                -{money(discountIncGst)} off the inc-GST total.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="rounded-none"
            onClick={() => onOpenChange(false)}
            data-testid="negotiate-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            disabled={!valid}
            onClick={() => onApply({ agreedIncGst: achievedTotal, discountExGst, reason })}
            data-testid="negotiate-apply"
          >
            Add discount line
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
